import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { PaperPlaneRight, Paperclip, PaintBrush, Briefcase, ShieldCheck, X } from "@phosphor-icons/react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const ROLES = {
  designer: { name: "Riya", title: "Creative Designer", icon: PaintBrush, cls: "text-ai", bg: "bg-ai/10", hint: "Discuss the image — attach competitor samples and ask her to redesign" },
  manager: { name: "Arjun", title: "Ads Manager", icon: Briefcase, cls: "text-[#0052FF]", bg: "bg-[#0052FF]/10", hint: "Discuss headline, copy, CTA, ad set or budget — he updates the package" },
  senior: { name: "Meera", title: "Senior Manager", icon: ShieldCheck, cls: "text-[#b07600]", bg: "bg-warning/10", hint: "Debate her review or ask her to send it back to Riya/Arjun" },
};

const fmtTime = (iso) => new Date(iso).toLocaleString("en-IN", { hour: "2-digit", minute: "2-digit" });

export default function TeamChat({ job, onClose, onChanged }) {
  const [role, setRole] = useState("designer");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback((r) => {
    api.get(`/team/jobs/${job.id}/chat/${r}`).then(({ data }) => setMessages(data.messages || [])).catch(() => {});
  }, [job.id]);

  useEffect(() => { setMessages([]); load(role); }, [role, load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, sending]);

  const pickFiles = (e) => {
    const chosen = Array.from(e.target.files || []).slice(0, 3 - files.length);
    chosen.forEach((f) => {
      if (f.size > 4 * 1024 * 1024) { toast.error(`${f.name} is over 4MB`); return; }
      const reader = new FileReader();
      reader.onload = () => setFiles((prev) => [...prev, { name: f.name, dataUrl: reader.result }].slice(0, 3));
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const send = async () => {
    const msg = text.trim();
    if (!msg && files.length === 0) return;
    const r = ROLES[role];
    setSending(true);
    setMessages((prev) => [...prev, { sender: "user", text: msg, image_ids: [], at: new Date().toISOString(), _local: files.map((f) => f.dataUrl) }]);
    setText("");
    const imgs = files.map((f) => f.dataUrl);
    setFiles([]);
    try {
      const { data } = await api.post(`/team/jobs/${job.id}/chat/${role}`, { message: msg || "See the attached reference images.", images: imgs });
      load(role);
      if (data.action_taken === "regenerate_image") { toast.success("Riya is redesigning the image — the new version will land back in your approval queue (~1 min)", { duration: 9000 }); onChanged?.(); }
      if (data.action_taken === "update_package") { toast.success("Arjun updated the ad — Meera is re-reviewing it now", { duration: 8000 }); onChanged?.(); }
      if (data.action_taken?.startsWith("sent_back")) { toast.success(`Meera sent it back to ${data.action_taken.endsWith("designer") ? "Riya" : "Arjun"}`, { duration: 8000 }); onChanged?.(); }
      if (data.action_taken === "approve") { toast.success("Meera re-approved — awaiting your final sign-off"); onChanged?.(); }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail), { duration: 8000 });
      load(role);
    } finally {
      setSending(false);
    }
  };

  const R = ROLES[role];

  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col gap-0" data-testid="team-chat-sheet">
        <div className="px-5 pt-5 pb-3 border-b border-[#E5E7EB]">
          <SheetTitle className="font-heading text-base font-extrabold tracking-tight text-[#0A0D14]">Talk to the team</SheetTitle>
          <p className="text-[11px] text-[#8a90a0] mt-0.5 truncate">{job.campaign_name} · {job.date}</p>
          <Tabs value={role} onValueChange={setRole} className="mt-3">
            <TabsList className="bg-[#F3F4F6] rounded-lg p-1 w-full">
              {Object.entries(ROLES).map(([key, r]) => {
                const Icon = r.icon;
                return (
                  <TabsTrigger key={key} value={key} data-testid={`chat-tab-${key}`} className="flex-1 rounded-md text-xs font-bold gap-1">
                    <Icon size={13} weight="duotone" /> {r.name}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#FAFAFB]" data-testid="chat-messages">
          <div className="text-center text-[11px] text-[#8a90a0] px-6">{R.hint}</div>
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed ${
                m.sender === "user" ? "bg-[#0A0D14] text-white rounded-br-sm" : "bg-white border border-[#E5E7EB] text-[#0A0D14] rounded-bl-sm"}`}>
                {m.sender === "agent" && <div className={`text-[10px] font-bold uppercase tracking-[0.12em] mb-1 ${R.cls}`}>{R.name} · {R.title}</div>}
                <div className="whitespace-pre-line">{m.text}</div>
                {m.tools_used?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1" data-testid="chat-tools-used">
                    {[...new Set(m.tools_used)].map((t, k) => (
                      <span key={k} className="rounded-full bg-[#F3F4F6] text-[#8a90a0] text-[9px] font-bold px-2 py-0.5 font-mono">⚒ {t}</span>
                    ))}
                  </div>
                )}
                {(m.image_ids?.length > 0 || m._local?.length > 0) && (
                  <div className="flex gap-1.5 mt-2 flex-wrap">
                    {(m._local || m.image_ids || []).map((src, k) => (
                      <img key={k} src={m._local ? src : `${API}/team/chat-images/${src}`} alt="reference" className="h-16 w-16 rounded-lg object-cover border border-white/20" />
                    ))}
                  </div>
                )}
                {m.action_taken && <div className="mt-1.5 text-[10px] font-bold text-[#00953e]">✓ action executed: {m.action_taken.replace(/_/g, " ")}</div>}
                <div className={`text-[9px] mt-1 ${m.sender === "user" ? "text-white/50" : "text-[#c0c4cf]"}`}>{fmtTime(m.at)}</div>
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-white border border-[#E5E7EB] rounded-2xl rounded-bl-sm px-4 py-3 text-[12px] text-[#8a90a0]">
                <span className={`font-bold ${R.cls}`}>{R.name}</span> is typing<span className="animate-pulse">…</span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="border-t border-[#E5E7EB] bg-white p-3">
          {files.length > 0 && (
            <div className="flex gap-2 mb-2 flex-wrap">
              {files.map((f, i) => (
                <div key={i} className="relative">
                  <img src={f.dataUrl} alt={f.name} className="h-14 w-14 rounded-lg object-cover border border-[#E5E7EB]" />
                  <button onClick={() => setFiles(files.filter((_, k) => k !== i))}
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-[#0A0D14] text-white flex items-center justify-center"><X size={9} weight="bold" /></button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-2">
            {(role === "designer" || role === "senior") && (
              <>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={pickFiles} data-testid="chat-file-input" />
                <button onClick={() => fileRef.current?.click()} data-testid="chat-attach-button" title="Attach competitor samples"
                  className="h-10 w-10 shrink-0 rounded-full border border-[#E5E7EB] flex items-center justify-center text-[#525866] hover:text-ai hover:border-ai/40 transition-colors">
                  <Paperclip size={17} />
                </button>
              </>
            )}
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} data-testid="chat-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Message ${R.name}…`} className="min-h-10 max-h-28 rounded-xl resize-none text-sm flex-1" />
            <Button onClick={send} disabled={sending || (!text.trim() && files.length === 0)} data-testid="chat-send-button"
              className="h-10 w-10 shrink-0 rounded-full bg-ai hover:bg-[#5457e0] text-white p-0">
              <PaperPlaneRight size={16} weight="fill" />
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
